import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  mapAxeExtensionIssueToUniversalIssue,
  mapIssueToSavedIssue
} from '@deque/axe-devtools-issues-utils';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { IconButton, Loader } from '@deque/cauldron-react';
import { type UniversalIssueWithScreenshot } from '@deque/axe-devtools-issues-utils';

import useIssuesFilter, {
  getBaseFilters,
  getSearchParamsFromFilters
} from '../../common/hooks/useIssuesFilter';
import { useTest } from '../contexts/Test';
import IssueFilters from '../../common/components/issues/IssueFilters';
import PageTitle from '../../common/components/PageTitle';
import { encodeQueryParams } from '../../common/utils/encodeQueryParams';
import ExportIssuesModal from '../components/ExportIssuesModal';
import ErrorMessage from '../../app/components/issue/ErrorMessage';
import { getIssuesWithUniversalMatches } from '../utils/getIssuesWithUniversalMatches';
import { useAuthContext } from '../../common/contexts/auth';
import { useConfiguration } from '../../common/contexts/Configuration';
import IssuesPanel from '../../common/components/issues/IssuesPanel';
import CopyToClipboard from '../../common/components/CopyToClipboard';
import ZeroStatePanel from '../../common/components/issues/ZeroStatePanel';
import useSharedTestAnalyticsData from '../hooks/useSharedTestAnalyticsData';
import { useAxeDevtoolsProAnalytics } from '../../common/contexts/analytics';
import ShareToIntegrationButton from '../../common/components/issues/ShareToIntegrationButton';
import {
  PRODUCT_URLS,
  ProductSlugs,
  SupportedShareableIssueIntegrationProductSlugs
} from '../../common/constants';
import { useIntegrations } from '../../app/hooks/useIntegrations';
import styles from './Issues.css';
import useIntegrationConnections from '../../app/hooks/useIntegrationConnections';
import { filterConnectedIntegrationSlugs } from '../../common/utils/integrations-client/filterConnectedIntegrationSlugs';

const IssuesPage: React.FC = () => {
  const { t } = useTranslation();
  const { loading: authLoading, error: authError, user } = useAuthContext();
  const { loading, error, test, issues, manifests, refetch } = useTest();
  const { loading: configurationLoading, loadingError: configurationError } =
    useConfiguration();
  const {
    enabledIntegrationProductSlugs,
    activeEnterprise,
    canAccessIntegrationConfiguration
  } = useIntegrations();

  const {
    data: connections,
    loading: connectionsLoading,
    error: connectionsError
  } = useIntegrationConnections(
    activeEnterprise?.id,
    user?.token,
    !canAccessIntegrationConfiguration
  );

  const connectedIntegrations = filterConnectedIntegrationSlugs(
    enabledIntegrationProductSlugs,
    connections
  ).filter(integration =>
    SupportedShareableIssueIntegrationProductSlugs.includes(integration)
  );

  const universalIssues: UniversalIssueWithScreenshot[] = useMemo(() => {
    return issues.map(issue => ({
      ...mapAxeExtensionIssueToUniversalIssue(mapIssueToSavedIssue(issue)),
      screenshotId: issue.screenshot_id,
      boundingBox: issue.bounding_box,
      testUrl: issue.test_url ?? ''
    }));
  }, [issues]);

  const { filteredIssues: filteredUniversalIssues, filters } =
    useIssuesFilter(universalIssues);

  const analytics = useAxeDevtoolsProAnalytics();
  const {
    ready: analyticsReady,
    data: analyticsData,
    filterData: analyticsFilterData,
    testId
  } = useSharedTestAnalyticsData({ filters });

  const [showExportModal, setShowExportModal] = useState(false);

  const isFirstDisplayedIssue = useRef<boolean>(true);

  /*
    The export code still expects TestRecordIssues, but the filtering code returns UniversalIssues.
    For now, we filter TestRecordIssues by the filtered universal issues for backwards compatibility.
    This is a temporary solution that we will remove once the export code is converted to universal issues.
  */
  const filteredIssues = useMemo(
    () => getIssuesWithUniversalMatches(issues, filteredUniversalIssues),
    [issues, filteredUniversalIssues]
  );

  useEffect(() => {
    if (analyticsReady && analyticsFilterData) {
      analytics.viewSharedTestIssues({
        ...analyticsData,
        ...analyticsFilterData
      });
    }
  }, [analyticsReady, testId]);

  const onCopyUrl = () => {
    if (analyticsReady) {
      analytics.sharedTestCopyUrl({
        ...analyticsData,
        ...analyticsFilterData
      });
    }
  };

  if (loading || configurationLoading || authLoading || connectionsLoading) {
    return (
      <>
        <PageTitle title={t('Loading issues')} />
        <Loader label={t('Loading issues...')} />
      </>
    );
  }

  if (error || !test || configurationError || authError || connectionsError) {
    return (
      <>
        <PageTitle title={t('Error loading issues')} />
        <ErrorMessage
          error={t(
            'There was an error loading your issues. Please refresh or try again later...'
          )}
        />
      </>
    );
  }

  const testName = test.name || t('Test');
  const pageTitle = t('Viewing {{testName}} issues', { testName });
  const testSearchParams = getSearchParamsFromFilters(getBaseFilters(filters));
  const testLink = `${
    PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].root
  }/tests/${test.id}?${testSearchParams}`;
  const issuesSearchParams = getSearchParamsFromFilters(filters);
  const issuesLink = `${window.location.origin}${
    window.location.pathname
  }?${encodeQueryParams(issuesSearchParams)}`;

  const ruleIdCounters: Record<string, number> = {};

  const filteredUniversalIssuesWithShareURL = filteredUniversalIssues.map(
    issue => {
      const { ruleId } = issue;

      if (!ruleIdCounters?.[ruleId]) {
        ruleIdCounters[ruleId] = 1;
      }

      const issueIndex = ruleIdCounters[ruleId];
      ruleIdCounters[ruleId] += 1;

      const url = new URL(issuesLink);
      url.searchParams.append('issueIndex', issueIndex.toString());
      url.searchParams.append('ruleId', ruleId);

      return { ...issue, shareURL: url.toString() };
    }
  );

  const onIssueView = (issue: UniversalIssueWithScreenshot) => {
    // do not report first issue loaded as per https://github.com/dequelabs/walnut/issues/5099
    if (!isFirstDisplayedIssue.current && analyticsReady && issue.issueId) {
      analytics.viewSharedTestIssue({
        ...analyticsData,
        issueId: issue.issueId
      });
    }
    isFirstDisplayedIssue.current = false;
  };

  return (
    <div className={styles.wrap}>
      <PageTitle title={pageTitle} />
      <div className={styles.header}>
        <IconButton
          className={styles.backButton}
          icon="arrow-left"
          as={RouterLink}
          to={testLink}
          label={t('Back to {{testName}}', { testName })}
        />
        <h1>{pageTitle}</h1>
        <CopyToClipboard
          onCopy={onCopyUrl}
          text={issuesLink}
          confirmationMessage={t(
            'A link for {{testName}} issues has been copied to clipboard',
            {
              testName
            }
          )}
        >
          <IconButton
            icon="link"
            label={t('Copy link for {{testName}} issues to clipboard', {
              testName
            })}
            tooltipProps={{
              placement: 'bottom'
            }}
          />
        </CopyToClipboard>
        {connectedIntegrations.map(integration => (
          <ShareToIntegrationButton
            key={`share-all-${integration}`}
            multiple={true}
            connections={connections}
            integrationProductSlug={integration}
            issues={filteredUniversalIssuesWithShareURL}
            testId={test.id}
            issuesProductSourceSlug={ProductSlugs.axeDevToolsExtension}
            onIntegrationShare={refetch}
          />
        ))}
      </div>
      <IssueFilters filters={filters} manifests={manifests}>
        <IconButton
          icon="download"
          label={t('Export Issues')}
          onClick={() => setShowExportModal(true)}
        />
      </IssueFilters>
      {showExportModal && (
        <ExportIssuesModal
          issues={filteredIssues}
          test={test}
          onClose={() => setShowExportModal(false)}
        />
      )}
      {filteredUniversalIssues.length === 0 ? (
        <ZeroStatePanel>
          {t(
            'There are 0 total issues for {{testName}} saved test record. Nice work!',
            { testName }
          )}
        </ZeroStatePanel>
      ) : (
        <IssuesPanel
          issues={filteredUniversalIssuesWithShareURL}
          onIssueView={onIssueView}
          onIntegrationShare={refetch}
          issuesProductSourceSlug={ProductSlugs.axeDevToolsExtension}
          testId={test.id}
        />
      )}
    </div>
  );
};

export default IssuesPage;
